import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useAuth from "@/hooks/useAuth";
import placesApi from "@/api/placesApi";
import PlaceResponse from "@/model/PlaceResponse";
import "@/components/PlacesSelect/PlacesSelect.scss";

// Places are a short admin-curated list, so one page holds them all.
const PLACES_PAGE_SIZE = 100;

interface PlacesSelectProps {
  value: string[];
  onChange: (placeIds: string[]) => void;
}

function PlacesSelect({ value, onChange }: PlacesSelectProps) {
  const { t } = useTranslation();
  const { session } = useAuth();

  const [places, setPlaces] = useState<PlaceResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!session) {
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(false);

    placesApi
      .listPlaces(session.accessToken, 0, PLACES_PAGE_SIZE)
      .then((result) => {
        if (!cancelled) {
          setPlaces(result.content ?? []);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setError(true);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [session]);

  const togglePlace = (placeId: string) => {
    onChange(
      value.includes(placeId)
        ? value.filter((id) => id !== placeId)
        : [...value, placeId]
    );
  };

  return (
    <div className="places-select">
      <div className="places-select__options">
        {places
          .filter(
            (place): place is PlaceResponse & { id: string } => Boolean(place.id)
          )
          .map((place) => (
            <label key={place.id} className="places-select__option">
              <input
                type="checkbox"
                checked={value.includes(place.id)}
                onChange={() => togglePlace(place.id)}
              />
              {place.name ?? place.id}
            </label>
          ))}
      </div>
      {isLoading && (
        <p className="places-select__help">{t("places.select.loading")}</p>
      )}
      {error && (
        <p className="places-select__error">{t("places.select.loadError")}</p>
      )}
      {!isLoading && !error && places.length === 0 && (
        <p className="places-select__help">{t("places.select.empty")}</p>
      )}
    </div>
  );
}

export default PlacesSelect;
export type { PlacesSelectProps };
